import { inject, Injectable, NgZone, OnDestroy } from '@angular/core';

import { CB2_PREVIEW_MESSAGE_SOURCE_OUT, Cb2PreviewOutboundMessage } from './cb2-preview-protocol';
import { Cb2PreviewStateService } from './cb2-preview-state.service';
import { Cb2BlocHighlightService } from './cb2-bloc-highlight.service';

/**
 * While studio pick mode is on, preview interactions must only select a block.
 * Child widgets (buttons, inputs, links, …) still receive bubble-phase events
 * before the per-block pick directive, so side effects (navigation, virtual
 * keyboard, …) fire first. This guard listens in the *capture* phase on
 * `document` and swallows those events before they reach the target.
 *
 * Scrolling is preserved: we never call `preventDefault` on wheel/touchmove,
 * and on pointer/touch start we only `preventDefault` for focusable/interactive
 * targets (so focus + activation are blocked without killing native scroll).
 */
@Injectable({ providedIn: 'root' })
export class Cb2PreviewPickGuardService implements OnDestroy {
  private readonly ngZone = inject(NgZone);
  private readonly previewState = inject(Cb2PreviewStateService);
  private readonly highlight = inject(Cb2BlocHighlightService);

  private armed = false;
  private readonly unlisteners: Array<() => void> = [];

  ngOnDestroy(): void {
    this.disarm();
  }

  /** Arm or disarm capture listeners to match the dashboard pick toggle. */
  sync(enabled: boolean): void {
    if (enabled) {
      this.arm();
      return;
    }
    this.disarm();
  }

  private arm(): void {
    if (this.armed || !this.previewState.isActive()) return;
    this.armed = true;

    this.ngZone.runOutsideAngular(() => {
      this.blurActiveElement();

      // Selection + hard block (must not reach widget handlers).
      for (const type of SELECT_AND_BLOCK_EVENTS) {
        this.listenCapture(type, event => this.onSelectAndBlock(event));
      }

      // Soft block: stop JS handlers; preventDefault only when it would focus
      // or activate a control (keeps native scrolling working).
      for (const type of BLOCK_POINTER_EVENTS) {
        this.listenCapture(type, event => this.onBlockPointer(event));
      }

      this.listenCapture('focusin', event => this.onFocusIn(event));
      this.listenCapture('submit', event => this.swallow(event, true));
      this.listenCapture('dragstart', event => this.swallow(event, true));

      // Hover highlight is painted as a fixed overlay so `.cb2-pick-bloc`
      // wrappers can stay `display: contents` (layout identical to production).
      this.listenCapture('mouseover', event => this.onHover(event));
      this.listenCapture('mouseout', event => this.onHoverOut(event));
    });
  }

  private disarm(): void {
    if (!this.armed) return;
    this.armed = false;
    while (this.unlisteners.length > 0) {
      this.unlisteners.pop()?.();
    }
    this.highlight.reset();
  }

  private listenCapture(type: string, handler: (event: Event) => void): void {
    document.addEventListener(type, handler, CAPTURE);
    this.unlisteners.push(() => document.removeEventListener(type, handler, CAPTURE));
  }

  private onSelectAndBlock(event: Event): void {
    if (!this.isPickLive()) return;

    this.swallow(event, true);

    if (event.type !== 'click') return;

    const blocKey = this.findBlocKey(event.target);
    if (!blocKey) return;

    // Own the postMessage here to avoid a circular DI with PreviewBridgeService.
    this.ngZone.run(() => this.emitSelectBloc(blocKey));
  }

  private emitSelectBloc(blocKey: string): void {
    if (!this.isPickLive()) return;
    const parent = window.parent;
    if (!parent) return;

    const message: Cb2PreviewOutboundMessage = {
      source: CB2_PREVIEW_MESSAGE_SOURCE_OUT,
      type: 'select-bloc',
      blocKey,
    };
    parent.postMessage(message, '*');
  }

  private onHover(event: Event): void {
    if (!this.isPickLive()) return;

    const target = event.target;
    const wrapper =
      target instanceof Element ? target.closest<HTMLElement>(`[${CB2_BLOC_KEY_ATTR}]`) : null;
    this.highlight.setHoverTarget(wrapper);
  }

  private onHoverOut(event: Event): void {
    if (!this.isPickLive()) return;

    // Pointer left the document (no related target): clear the highlight.
    if (event instanceof MouseEvent && event.relatedTarget === null) {
      this.highlight.setHoverTarget(null);
    }
  }

  private onBlockPointer(event: Event): void {
    if (!this.isPickLive()) return;

    const preventDefault = this.isInteractiveTarget(event.target);
    this.swallow(event, preventDefault);
  }

  private onFocusIn(event: Event): void {
    if (!this.isPickLive()) return;

    this.swallow(event, true);
    const target = event.target;
    if (target instanceof HTMLElement) {
      target.blur();
    }
  }

  private swallow(event: Event, preventDefault: boolean): void {
    if (preventDefault && event.cancelable) {
      event.preventDefault();
    }
    event.stopImmediatePropagation();
  }

  private isPickLive(): boolean {
    return this.previewState.isActive() && this.previewState.pickEnabled();
  }

  private findBlocKey(target: EventTarget | null): string | null {
    if (!(target instanceof Element)) return null;
    const host = target.closest<HTMLElement>(`[${CB2_BLOC_KEY_ATTR}]`);
    return host?.getAttribute(CB2_BLOC_KEY_ATTR) ?? null;
  }

  private isInteractiveTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) return false;
    return !!target.closest(INTERACTIVE_SELECTOR);
  }

  private blurActiveElement(): void {
    const active = document.activeElement;
    if (active instanceof HTMLElement && active !== document.body) {
      active.blur();
    }
  }
}

// `passive: false` is required: Chrome registers document `touchstart` as
// passive by default, which makes preventDefault() a no-op and spams
// "Unable to preventDefault inside passive event listener" in the console.
const CAPTURE: AddEventListenerOptions = { capture: true, passive: false };
const CB2_BLOC_KEY_ATTR = 'data-cb2-bloc-key';

const SELECT_AND_BLOCK_EVENTS = ['click', 'auxclick', 'dblclick', 'contextmenu'] as const;
const BLOCK_POINTER_EVENTS = ['pointerdown', 'mousedown', 'touchstart'] as const;

/**
 * Targets that would take focus or activate a control on pointer down.
 * Broader than native form controls so custom kiosk widgets are covered too.
 */
const INTERACTIVE_SELECTOR = [
  'a[href]',
  'button',
  'input',
  'select',
  'textarea',
  'label',
  'summary',
  'option',
  '[contenteditable=""]',
  '[contenteditable="true"]',
  '[tabindex]',
  '[role="button"]',
  '[role="link"]',
  '[role="textbox"]',
  '[role="searchbox"]',
  '[role="checkbox"]',
  '[role="radio"]',
  '[role="switch"]',
  '[role="menuitem"]',
  '[role="option"]',
  '[role="slider"]',
  '[role="combobox"]',
].join(',');
